import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import navStyle from './navbar.module.scss'
import SubMenu from './SubMenu'
import expProfileIcon from '../../assets/icons/experienceprofile-favicon.svg'
import ideasBankIcon from '../../assets/icons/ideasbank_download.png'
import LogoIcon from '../../assets/icons/logo.svg'
import { useMenu } from '../../hooks/useMenu'
import { MenuItem } from '../../types/types'

interface NavbarProps {
  isOpen: boolean
  onClose: () => void
}

const BOTTOM_LINKS = [
  { icon: ideasBankIcon, label: 'Ideas Bank', link: '/ideas-bank' },
  {
    icon: expProfileIcon,
    label: 'Experience Profile',
    link: '/experience-profile',
  },
]

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M5.70711 4.29289C5.31658 3.90237 4.68342 3.90237 4.29289 4.29289C3.90237 4.68342 3.90237 5.31658 4.29289 5.70711L8.58579 10L4.29289 14.2929C3.90237 14.6834 3.90237 15.3166 4.29289 15.7071C4.68342 16.0976 5.31658 16.0976 5.70711 15.7071L10 11.4142L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L11.4142 10L15.7071 5.70711C16.0976 5.31658 16.0976 4.68342 15.7071 4.29289C15.3166 3.90237 14.6834 3.90237 14.2929 4.29289L10 8.58579L5.70711 4.29289Z"
      fill="currentColor"
    />
  </svg>
)

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M8 4L14 10L8 16"
      stroke="currentColor"
      strokeWidth="2"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const isInternalPath = (path?: string): path is string => !!path && path.startsWith('/')

export const isRouteActive = (pathname: string, path?: string): boolean => {
  if (!isInternalPath(path)) return false
  const current = pathname.toLowerCase()
  const target = path.toLowerCase()
  return current === target || current.startsWith(`${target}/`)
}

const collectPaths = (items: MenuItem[]): string[] =>
  items.flatMap(item => [...(isInternalPath(item.path) ? [item.path] : []), ...collectPaths(item.children ?? [])])

// Longest matching path wins, so '/a/b' beats its prefix '/a'
export const findActivePath = (pathname: string, items: MenuItem[]): string | undefined =>
  collectPaths(items)
    .filter(path => isRouteActive(pathname, path))
    .sort((a, b) => b.length - a.length)[0]

const isHomeRoute = (pathname: string): boolean => ['/', '/homepage'].includes(pathname.toLowerCase())

const SKELETON_WIDTHS = ['62%', '78%', '54%', '70%', '58%']

const MenuSkeleton = () => (
  <ul className={`${navStyle.skeletonList} space-y-1`} role="status" aria-label="Loading menu">
    {SKELETON_WIDTHS.map((width, i) => (
      <li key={i} className={navStyle.skeletonRow} aria-hidden="true">
        <span className={navStyle.skeletonBar} style={{ width }} />
      </li>
    ))}
  </ul>
)

const Navbar = ({ isOpen, onClose }: NavbarProps): JSX.Element => {
  const [active, setActive] = useState<string>('')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { menu, isLoading } = useMenu()

  // Reset submenu when nav opens
  useEffect(() => {
    if (isOpen) setActive('')
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const activePath = useMemo(() => findActivePath(pathname, menu), [pathname, menu])

  const containsActivePath = useCallback(
    (item: MenuItem): boolean =>
      !!activePath && (item.path === activePath || !!item.children?.some(child => containsActivePath(child))),
    [activePath],
  )

  const handleMenuClick = useCallback((item: MenuItem) => {
    if (item.children) {
      setActive(prev => (prev === item.id ? '' : item.id))
    } else {
      setActive(item.id)
    }
  }, [])

  const handleHomeClick = useCallback(() => {
    navigate('/')
    onClose()
  }, [navigate, onClose])

  const activeMenu = useMemo(() => menu.find(m => m.id === active), [menu, active])

  return (
    <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black/40 z-[899] lg:hidden" onClick={onClose} aria-hidden="true" />}

      <nav
        aria-label="Main navigation"
        aria-hidden={!isOpen}
        className={`${navStyle.navbarContainer} fixed top-0 left-0 w-full sm:w-[320px] lg:w-auto h-screen bg-[#f7f7f7] shadow-xl
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className={`${navStyle.navbarHeader} px-4 py-3 sm:px-6 lg:px-8`}>
          <button onClick={onClose} type="button" aria-label="Close navigation" className={navStyle.closeIcon}>
            <CloseIcon />
          </button>
          <img src={LogoIcon} alt="Home" height="24" width="163" />
        </div>

        {/* Body */}
        <div
          className={`${navStyle.navbarMenu} flex flex-col lg:flex-row h-full p-5 sm:p-6 lg:p-[95px_72px_32px] gap-3 lg:gap-12`}
        >
          {/* LEFT — primary menu */}
          <div
            className={`${navStyle.navbarMenuPlayground} w-full lg:w-[272px] order-2 lg:order-1 ${
              active ? 'hidden lg:flex' : 'flex'
            }`}
          >
            <h2 className="subtitle-l">Playground</h2>

            <button
              onClick={handleHomeClick}
              className={`flex justify-between items-center w-full px-2 py-1 rounded cursor-pointer text-left ${
                isHomeRoute(pathname) ? navStyle.activeMenuItem : ''
              }`}
            >
              <span className="body-s">Home</span>
            </button>

            <hr className={`my-1.5 ${navStyle.divider}`} />

            {isLoading ? (
              <MenuSkeleton />
            ) : (
              <ul className={`space-y-1 ${navStyle.fadeIn}`}>
                {menu.map((item: MenuItem) => (
                  <li key={item.id}>
                    <button
                      onClick={() => handleMenuClick(item)}
                      className={`flex justify-between items-center w-full px-2 py-1 rounded cursor-pointer text-left ${
                        active === item.id || containsActivePath(item) ? navStyle.activeMenuItem : ''
                      }`}
                    >
                      <span className="body-s">{item.label}</span>
                      {item.children && (
                        <span className={navStyle.rightArrow}>
                          <ChevronRightIcon />
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className={`flex flex-col gap-[2px] mt-1 ${navStyle.menuBottom}`}>
              {BOTTOM_LINKS.map(({ icon, label, link }) => (
                <div
                  key={label}
                  className={`flex items-center ${isRouteActive(pathname, link) ? navStyle.activeBottomItem : ''}`}
                >
                  <img src={icon} alt={label} className={navStyle.menuIcon} />
                  {link ? (
                    <Link
                      to={link}
                      className="body-s px-2.5 py-2.5 w-full"
                      onClick={onClose}
                      aria-current={isRouteActive(pathname, link) ? 'page' : undefined}
                    >
                      {label}
                    </Link>
                  ) : (
                    <span className="body-s px-2.5 py-2.5 w-full">{label}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — submenu panel */}
          <div
            className={`w-full lg:w-[272px] lg:flex-1 min-h-full order-1 lg:order-2 mt-2 lg:mt-0 ${
              active ? 'flex flex-col' : 'hidden lg:hidden'
            }`}
          >
            <div className={navStyle.submenuHead}>
              <h3 className="body-m">{activeMenu?.label}</h3>
            </div>
            <SubMenu
              items={activeMenu?.children}
              activePath={activePath}
              onClose={onClose}
              onBack={() => setActive('')}
            />
          </div>
        </div>
      </nav>
    </>
  )
}

export default Navbar
